import type { KeyboardEvent, MouseEvent as ReactMouseEvent } from "react";
import { useLayoutEffect, useRef, useState } from "react";
import Sortable from "sortablejs";
import { useTranslation } from "react-i18next";
import type { ReactSortableProps } from "react-sortablejs";
import type { SortableEvent } from "sortablejs";
import { useAppSelector } from "src/common/state/hooks";
import { getShowStandardModal } from "src/common/state/slice/modal/modalSlice";
import usePrefersReducedMotion from "src/common/util/usePrefersReducedMotion";
import { selectActiveTour } from "src/features/onboarding/state/onboardingSlice";
import type { SidebarItemDef, SidebarItemId, SidebarSectionId } from "./sidebarConfig";
import type { useSidebarFavorites } from "./useSidebarFavorites";

export interface ISidebarSortableItem {
  id: SidebarItemId;
}

type SortableListProps = Omit<ReactSortableProps<ISidebarSortableItem>, "list" | "children">;

const SIDEBAR_LIST_ID_PREFIX = "sidebar-list-";
export const SIDEBAR_FAVORITES_LIST_ID = `${SIDEBAR_LIST_ID_PREFIX}favorites`;
export const SIDEBAR_FAVORITES_DND_HELP_ID = "sidebar-favorites-dnd-help";

const REORDER_KEYS = ["ArrowUp", "ArrowDown", "Home", "End"];

const toSortableItems = (items: SidebarItemDef[]): ISidebarSortableItem[] =>
  items.map((item) => ({ id: item.id }));

const readItemId = (element: HTMLElement | null | undefined) =>
  element?.dataset.sidebarItemId as SidebarItemId | undefined;

const readPoint = (event: Event | undefined) => {
  if (!event) return null;
  if ("changedTouches" in event) {
    const touch = (event as TouchEvent).changedTouches[0];
    return touch ? { x: touch.clientX, y: touch.clientY } : null;
  }
  if ("clientX" in event) {
    const { clientX, clientY } = event as MouseEvent;
    return { x: clientX, y: clientY };
  }
  return null;
};

const isOverFavoritesList = (event: Event | undefined) => {
  const point = readPoint(event);
  const list = document.getElementById(SIDEBAR_FAVORITES_LIST_ID);
  if (!point || !list) return false;
  const rect = list.getBoundingClientRect();
  return (
    point.x >= rect.left && point.x <= rect.right && point.y >= rect.top && point.y <= rect.bottom
  );
};

export const useSidebarDragAndDrop = (
  favorites: ReturnType<typeof useSidebarFavorites>,
  getTitle: (id: SidebarItemId) => string,
) => {
  const { t } = useTranslation();
  const prefersReducedMotion = usePrefersReducedMotion();
  const isModalOpen = useAppSelector(getShowStandardModal).show;
  const isTourActive = useAppSelector(selectActiveTour) !== null;

  const [isDragging, setIsDragging] = useState(false);
  const [dragForcedSection, setDragForcedSection] = useState<SidebarSectionId>();
  const [announcement, setAnnouncement] = useState("");
  const suppressClickRef = useRef(false);
  const draggedIdRef = useRef<SidebarItemId>();
  const gripRefs = useRef(new Map<SidebarItemId, HTMLButtonElement>());
  const pendingFocusRef = useRef<SidebarItemId>();

  const { favoriteItems } = favorites;
  const favoriteIds = favoriteItems.map((item) => item.id);
  const favoriteIdsRef = useRef(favoriteIds);
  favoriteIdsRef.current = favoriteIds;

  const disabled = isModalOpen || isTourActive || favorites.resyncing;

  useLayoutEffect(
    () => () => {
      document.querySelectorAll<HTMLElement>(`[id^="${SIDEBAR_LIST_ID_PREFIX}"]`).forEach((el) => {
        Sortable.get(el)?.destroy();
      });
    },
    [],
  );

  const orderKey = favoriteIds.join(",");
  useLayoutEffect(() => {
    const id = pendingFocusRef.current;
    if (!id) return;
    pendingFocusRef.current = undefined;
    gripRefs.current.get(id)?.focus();
  }, [orderKey]);

  const announceMoved = (id: SidebarItemId, order: SidebarItemId[]) =>
    setAnnouncement(
      t("sidebar.favorites.moved", {
        name: getTitle(id),
        position: order.indexOf(id) + 1,
        total: order.length,
      }),
    );

  const handleStart = (evt: SortableEvent) => {
    draggedIdRef.current = readItemId(evt.item);
    favorites.beginDrag();
    setIsDragging(true);
    if (evt.from.id !== SIDEBAR_FAVORITES_LIST_ID) setDragForcedSection("favorites");
  };

  const handleEnd = (evt: SortableEvent & { originalEvent?: Event }) => {
    const id = readItemId(evt.item);
    if (
      id &&
      evt.from.id !== SIDEBAR_FAVORITES_LIST_ID &&
      evt.to === evt.from &&
      favoriteIdsRef.current.includes(id) &&
      isOverFavoritesList(evt.originalEvent)
    ) {
      setAnnouncement(t("sidebar.favorites.alreadyFavorite", { name: getTitle(id) }));
    }
    draggedIdRef.current = undefined;
    favorites.endDrag();
    setIsDragging(false);
    setDragForcedSection(undefined);
    suppressClickRef.current = true;
    setTimeout(() => {
      suppressClickRef.current = false;
    }, 0);
  };

  const sharedOptions: SortableListProps = {
    setList: () => undefined,
    disabled,
    forceFallback: true,
    fallbackOnBody: true,
    fallbackTolerance: 5,
    delayOnTouchOnly: true,
    delay: 200,
    draggable: "[data-sidebar-item-id]",
    filter: "[data-sidebar-no-drag]",
    preventOnFilter: false,
    scroll: true,
    scrollSensitivity: 40,
    scrollSpeed: 12,
    animation: prefersReducedMotion ? 0 : 150,
    ghostClass: "opacity-40",
    chosenClass: "bg-blue-300",
    fallbackClass: "shadow-lg",
    onStart: handleStart,
    onEnd: handleEnd,
  };

  const handleFavoritesSetList = (next: ISidebarSortableItem[]) => {
    const nextIds = next.map((item) => item.id);
    const currentIds = favoriteIdsRef.current;
    if (nextIds.length === currentIds.length && nextIds.every((id, i) => id === currentIds[i])) {
      return;
    }
    const addedId = nextIds.find((id) => !currentIds.includes(id));
    if (addedId) {
      const position = nextIds.indexOf(addedId);
      favorites.addFavoriteAt(addedId, position);
      setAnnouncement(
        t("sidebar.favorites.added", { name: getTitle(addedId), position: position + 1 }),
      );
      return;
    }
    favorites.setFavoritesOrder(nextIds);
    const movedId = draggedIdRef.current ?? nextIds.find((id, i) => id !== currentIds[i]);
    if (movedId) announceMoved(movedId, nextIds);
  };

  const favoritesListProps = (items: SidebarItemDef[]) => ({
    ...sharedOptions,
    id: SIDEBAR_FAVORITES_LIST_ID,
    list: toSortableItems(items),
    setList: handleFavoritesSetList,
    group: {
      name: "sidebar",
      pull: false,
      put: (_to: unknown, _from: unknown, dragEl: HTMLElement) => {
        const id = readItemId(dragEl);
        return (
          dragEl.dataset.favoritable === "true" &&
          Boolean(id) &&
          !favoriteIdsRef.current.includes(id as SidebarItemId)
        );
      },
    },
    sort: true,
    className: isDragging
      ? `min-h-[28px]${
          items.length === 0 ? " rounded-md border border-dashed border-gray-400" : ""
        }`
      : undefined,
  });

  const sectionListProps = (sectionId: SidebarSectionId, items: SidebarItemDef[]) => ({
    ...sharedOptions,
    id: `${SIDEBAR_LIST_ID_PREFIX}${sectionId}`,
    list: toSortableItems(items),
    group: { name: "sidebar", pull: "clone" as const, put: false },
    sort: false,
  });

  const handleGripKeyDown = (id: SidebarItemId, event: KeyboardEvent<HTMLButtonElement>) => {
    if (!REORDER_KEYS.includes(event.key)) return;
    event.preventDefault();
    if (disabled) return;
    const order = favoriteIdsRef.current.filter((favId) => favId !== id);
    const from = favoriteIdsRef.current.indexOf(id);
    const target =
      event.key === "ArrowUp"
        ? Math.max(0, from - 1)
        : event.key === "ArrowDown"
        ? Math.min(order.length, from + 1)
        : event.key === "Home"
        ? 0
        : order.length;
    if (target === from) return;
    order.splice(target, 0, id);
    pendingFocusRef.current = id;
    favorites.setFavoritesOrder(order);
    announceMoved(id, order);
  };

  const registerGrip = (id: SidebarItemId) => (node: HTMLButtonElement | null) => {
    if (node) gripRefs.current.set(id, node);
    else gripRefs.current.delete(id);
  };

  const handleClickCapture = (event: ReactMouseEvent) => {
    if (!suppressClickRef.current) return;
    event.preventDefault();
    event.stopPropagation();
  };

  return {
    animationKey: String(sharedOptions.animation),
    disabled,
    isDragging,
    dragForcedSection,
    announcement,
    favoritesListProps,
    sectionListProps,
    handleGripKeyDown,
    registerGrip,
    handleClickCapture,
  };
};
